import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import styled from "styled-components";
import { BsArrowRepeat } from "react-icons/bs";
import { MdFileDownload } from "react-icons/md";
import { BsThreeDotsVertical } from "react-icons/bs";

const InvoicesTab = ({ partnerId }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const { AuthReducer: { auth } } = useSelector((state) => state);

  useEffect(() => {
    fetchPartnerInvoices();
  }, [partnerId]);

  const fetchPartnerInvoices = async () => {
    try {
      const response = await axios.get(`/api/admin/v1/partner-invoices/${partnerId}`, {
        headers: { Authorization: `Bearer ${auth?.token}` }
      });
      setInvoices(response.data.invoices || []);
    } catch (error) {
      console.error('Failed to fetch partner invoices');
    } finally {
      setLoading(false);
    }
  };


  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");

  const handleExportInvoice = async () => {
    try {
      const response = await axios.get(`/api/admin/v1/partner-invoices/${partnerId}/export`, {
        headers: { Authorization: `Bearer ${auth?.token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `partner-invoices-${partnerId}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      const csvData = filteredInvoices.map((invoice, index) => ({
        "SL. No.": index + 1,
        "Invoice #": invoice.invoice_number,
        "Issue Date": new Date(invoice.issue_date).toLocaleDateString(),
        "Due Date": new Date(invoice.due_date).toLocaleDateString(),
        "Billed To": invoice.billed_to_name,
        "Type": invoice.invoice_type,
        "Total Amount": `Rs.${parseFloat(invoice.total_amount).toLocaleString()}`,
        "Status": invoice.status
      }));
      const csv = [Object.keys(csvData[0]).join(','), ...csvData.map(row => Object.values(row).join(','))].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `partner-invoices-${partnerId}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    }
  };

  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setTypeFilter("all");
    setDateFilter("all");
  };

  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch = !searchTerm || 
      invoice.invoice_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.billed_to_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || invoice.status.toLowerCase() === statusFilter.toLowerCase();
    const matchesType = typeFilter === "all" || invoice.invoice_type.toLowerCase() === typeFilter.toLowerCase();
    return matchesSearch && matchesStatus && matchesType;
  });

  return (
    <Wrapper>
      <div className="card border-0 shadow-sm p-4">
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap">
          <div>
            <h4 className="text-dark mb-1 d-flex align-items-center">
              All Invoices
            </h4>
            <p className="text-muted mb-0">
              View and manage all generated invoices for this partner
            </p>
          </div>
          <div className="d-flex gap-3 mt-2 mt-md-0">
            <button className="btn btn-primary d-flex align-items-center" onClick={handleExportInvoice}>
              <MdFileDownload className="me-2" size={20} />
              Export Invoice
            </button>
          </div>
        </div>
        <div className="d-flex align-items-center flex-wrap gap-2 mb-3 filter-box">
          <input
            type="text"
            className="form-control"
            placeholder="Search ID, Customer, Service"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <select className="form-select" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)}>
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">Last Week</option>
            <option value="month">Last Month</option>
          </select>
          <select className="form-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All Statuses</option>
            <option value="paid">Paid</option>
            <option value="pending">Pending</option>
            <option value="overdue">Overdue</option>
          </select>
          <select className="form-select" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="all">All Types</option>
            <option value="booking">Booking</option>
            <option value="service">Service</option>
            <option value="package">Package</option>
          </select>
          <button className="btn btn-outline-secondary d-flex align-items-center gap-1" onClick={clearFilters}>
            <BsArrowRepeat size={16} />
            Clear Filters
          </button>
        </div>

        <TableWrapper>
          <table className="table table-bordered align-middle">
            <thead className="table-light">
              <tr>
                <th>Invoice #</th>
                <th>Issue Date</th>
                <th>Due Date</th>
                <th>Billed To</th>
                <th>Type</th>
                <th>Total Amount</th>
                <th>Status</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredInvoices.map((invoice, index) => (
                <tr key={index}>
                  <td><strong>{invoice.invoice_number}</strong></td>
                  <td>{new Date(invoice.issue_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                  <td>{new Date(invoice.due_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                  <td>{invoice.billed_to_name}</td>
                  <td>
                    <span className={`badge ${
                      invoice.invoice_type === 'booking' ? 'bg-primary-subtle text-primary' :
                      invoice.invoice_type === 'service' ? 'bg-success-subtle text-success' :
                      'bg-purple'
                    }`}>
                      {invoice.invoice_type}
                    </span>
                  </td>
                  <td><strong>₹{parseFloat(invoice.total_amount).toLocaleString()}</strong></td>
                  <td>
                    <span className={`badge ${
                      invoice.status.toLowerCase() === 'paid' ? 'bg-success-subtle text-success' :
                      'bg-warning-subtle text-warning'
                    }`}>
                      {invoice.status}
                    </span>
                  </td>
                  <td className="text-center"><BsThreeDotsVertical /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrapper>
      </div>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  .filter-box input,
  .filter-box select {
    width: 169px !important;
  }
`;

const TableWrapper = styled.div`
  .badge {
    padding: 6px 10px;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 500;
  }

  .bg-purple {
    background-color: rgba(155, 89, 182, 0.15);
    color: #9b59b6;
  }

  .table th,
  .table td {
    vertical-align: middle;
  }

  .text-center {
    text-align: center;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;
  }

  th,
  td {
    padding: 10px 12px;
    border: 1px solid #dee2e6;
    text-align: center;
    vertical-align: middle;
  }

  th {
    background-color: #f8f9fa;
    font-weight: 600;
  }
`;

export default InvoicesTab;